// PWA install prompt + offline banner + new-version toast.
// Mounted globally inside App.js so it's available on every route.
import React, { useEffect, useState } from "react";
import FlexxLogoImg from "@/components/FlexxLogoImg";

const T = {
  gold: "#C9A227", goldLight: "#E8B94F",
  red: "#FF4444", green: "#22C55E",
  bg: "#0C0C0C", border: "#2a2e36", text: "#EDE8DC", mute: "#888",
};
const LS_DISMISS_KEY = "flexx_pwa_install_dismissed_until";

export default function PwaShell() {
  const [installEvent, setInstallEvent] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [installed, setInstalled] = useState(() =>
    typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)")?.matches
  );
  const [online, setOnline] = useState(() => typeof navigator !== "undefined" ? navigator.onLine : true);
  const [updateReady, setUpdateReady] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // Capture beforeinstallprompt → keep the event so we can fire it on user click
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      const dismissedUntil = Number(localStorage.getItem(LS_DISMISS_KEY) || 0);
      if (Date.now() >= dismissedUntil) setShowInstall(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setShowInstall(false);
      setInstallEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Online/offline status
  useEffect(() => {
    const setOn = () => setOnline(true);
    const setOff = () => setOnline(false);
    window.addEventListener("online", setOn);
    window.addEventListener("offline", setOff);
    return () => {
      window.removeEventListener("online", setOn);
      window.removeEventListener("offline", setOff);
    };
  }, []);

  // SW update detection — listen for waiting worker
  useEffect(() => {
    if (!("serviceWorker" in navigator) || process.env.NODE_ENV !== "production") return;
    let cancelled = false;
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg || cancelled) return;
      const handle = (worker) => {
        worker.addEventListener("statechange", () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller) {
            setWaitingWorker(worker);
            setUpdateReady(true);
          }
        });
      };
      if (reg.waiting) { setWaitingWorker(reg.waiting); setUpdateReady(true); }
      reg.addEventListener("updatefound", () => { if (reg.installing) handle(reg.installing); });
    }).catch(() => {});
    // Reload once new SW takes control
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
    return () => { cancelled = true; };
  }, []);

  const onInstallClick = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    try {
      await installEvent.userChoice;
    } catch (_) { /* dismissed */ }
    setInstallEvent(null);
    setShowInstall(false);
  };
  const onDismissInstall = () => {
    // Snooze for 14 days
    localStorage.setItem(LS_DISMISS_KEY, String(Date.now() + 14 * 86400000));
    setShowInstall(false);
  };
  const applyUpdate = () => {
    if (!waitingWorker) return;
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
    // controllerchange handler will reload the page
  };

  return (
    <>
      {/* Offline indicator — top of viewport, fixed */}
      {!online && (
        <div data-testid="pwa-offline-banner" role="status" aria-live="polite"
          style={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 10500,
            background: T.red, color: "#fff",
            padding: "8px 14px", textAlign: "center",
            fontFamily: "monospace", fontSize: 12, letterSpacing: 1,
            borderBottom: `1px solid #00000033`,
          }}>
          ⚠️ You're offline — cached content is still available, but new lessons and live updates are paused.
        </div>
      )}

      {/* New version available toast */}
      {updateReady && (
        <div data-testid="pwa-update-toast" role="alert"
          style={{ ...toastStyle, borderColor: T.gold, background: `${T.gold}10` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "monospace", fontSize: 10, letterSpacing: 2, color: T.gold }}>NEW VERSION READY</div>
            <div style={{ color: T.text, fontSize: 13, marginTop: 4 }}>FLEXX has been updated. Refresh to get the latest.</div>
          </div>
          <button data-testid="pwa-update-refresh-btn" onClick={applyUpdate}
            style={{ ...primaryBtn, background: T.gold }}>Refresh</button>
        </div>
      )}

      {/* Install prompt — only shown when browser supports it AND not installed */}
      {!installed && showInstall && installEvent && (
        <div data-testid="pwa-install-toast" role="dialog" aria-label="Install FLEXX"
          style={{ ...toastStyle, bottom: updateReady ? 100 : 24 }}>
          <FlexxLogoImg alt="" style={{ height: 36, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: T.text, fontWeight: 700, fontSize: 14 }}>Install FLEXX as an app</div>
            <div style={{ color: T.mute, fontSize: 12, marginTop: 2, lineHeight: 1.4 }}>
              Faster launch · works offline · push notifications soon
            </div>
          </div>
          <button data-testid="pwa-install-dismiss-btn" onClick={onDismissInstall} style={ghostBtn}>Not now</button>
          <button data-testid="pwa-install-accept-btn" onClick={onInstallClick} style={primaryBtn}>Install</button>
        </div>
      )}
    </>
  );
}

const toastStyle = {
  position: "fixed",
  left: "50%", transform: "translateX(-50%)",
  bottom: 24,
  zIndex: 10800,
  background: T.bg, border: `1px solid ${T.border}`,
  borderRadius: 12,
  padding: "12px 14px",
  display: "flex", alignItems: "center", gap: 12,
  boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
  width: "min(560px, calc(100vw - 24px))",
  paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
};
const primaryBtn = {
  padding: "8px 14px", borderRadius: 6,
  background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
  color: "#0a0a0a", border: "none", cursor: "pointer",
  fontWeight: 800, fontFamily: "monospace", letterSpacing: 1, fontSize: 11, textTransform: "uppercase",
  flexShrink: 0,
};
const ghostBtn = {
  padding: "8px 12px", borderRadius: 6,
  background: "transparent", border: `1px solid ${T.border}`, color: T.mute,
  cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: 1,
  flexShrink: 0,
};
